import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router } from '@angular/router';
import { BasketService, SelectedAppliance } from '@basket';
import { CatalogueService, PriceBand } from '@catalogue';
import { CardComponent, LinkComponent } from '@components';
import { GADataService } from '@data-access';
import { ErrorRoutes } from '@features/error';
import {
  FormControlValidators,
  FormFieldComponent,
  FormFieldDirective,
  FormOption,
  FormSubmitButtonComponent,
  FormSubmitDirective,
  highlightInvalidControls,
  RadioComponent,
  SelectComponent,
} from '@forms';
import {
  provideTranslationDictionaryMapping,
  TranslatePipe,
  TranslateService,
} from '@i18n';
import { PosthogTrackingEvents, TrackingService } from '@tracking';
import { getMonth, getYear } from 'date-fns';
import posthog from 'posthog-js';
import { catchError, map, tap } from 'rxjs';
import {
  manufacturerFormField,
  priceBands,
  purchaseMonthFormField,
  purchasePriceFormField,
  purchaseYearFormField,
  wasBoughtWithin12MonthsPriceFormField,
} from '../../constants/multi-plan-appliance-details-form-fields.constant';
import { FeatureMultiPlanTranslationDictionary } from '../../i18n/feature-multi-plan-dictionary.interface';
import { translation } from '../../i18n/translation.constant';
import { ApplianceDetailsFormValue } from '../../models/appliance-details-form-value.type';
import { ApplianceDetailsForm } from '../../models/appliance-details-form.interface';
import { SelectedApplianceDetails } from '../../models/selected-appliance-details.interface';
import { ApplianceDetailsFormBuilder } from '../../services/appliance-details-form-builder/appliance-details-form-builder.service';
import { MultiPlanService } from '../../services/multi-plan/multi-plan.service';
import {
  adjustMonthForYearSelection,
  createMonthOptionsForYear,
} from '../../utils/multi-plan.utils';
@Component({
  selector: 'app-appliance-details-form',
  imports: [
    CardComponent,
    FormFieldComponent,
    FormFieldDirective,
    FormSubmitButtonComponent,
    FormSubmitDirective,
    LinkComponent,
    RadioComponent,
    ReactiveFormsModule,
    SelectComponent,
    TranslatePipe,
  ],
  templateUrl: './appliance-details-form.component.html',
  styleUrl: './appliance-details-form.component.scss',
  providers: [
    ApplianceDetailsFormBuilder,
    provideTranslationDictionaryMapping(translation),
  ],
})
export class ApplianceDetailsFormComponent {
  private readonly applianceDetailsFormBuilder = inject(
    ApplianceDetailsFormBuilder,
  );
  private readonly basketService = inject(BasketService);
  private readonly catalogueService = inject(CatalogueService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly trackingService = inject(TrackingService);
  private readonly gaDataService = inject(GADataService);
  private readonly multiPlanService = inject(MultiPlanService);
  private readonly router = inject(Router);
  private readonly translateService = inject(
    TranslateService<FeatureMultiPlanTranslationDictionary>,
  );

  readonly selectedAppliances = input<SelectedAppliance[]>();
  readonly applianceForm = this.createEmptyFormGroup();

  // Cache of brands by appliance code
  readonly brandsByApplianceCode = signal<
    Readonly<Record<string, FormOption[]>>
  >({});

  // Avoid duplicate fetches for the same appliance code
  private readonly loadingBrandsForApplianceCode = new Set<string>();

  // Tracks whether each appliance card should show the month select
  readonly shouldShowMonthByAppliance = signal<
    Readonly<Record<string, boolean>>
  >({});

  // Constants for form fields
  readonly formFields = {
    manufacturer: manufacturerFormField,
    purchaseMonth: purchaseMonthFormField,
    purchasePrice: purchasePriceFormField,
    purchaseYear: purchaseYearFormField,
    wasBoughtWithin12Months: wasBoughtWithin12MonthsPriceFormField,
  };
  readonly priceBands: PriceBand[] = priceBands;

  createYearOptions(showMonthFormField: boolean): FormOption<string>[] {
    const now = new Date();
    const currentYear = getYear(now);

    const startYear = showMonthFormField
      ? currentYear - 1 // include current year and last year
      : currentYear - 5; // go 5 back

    const endYear = showMonthFormField ? currentYear : currentYear - 1;

    return Array.from({ length: endYear - startYear + 1 }, (_, i) => {
      const year = (startYear + i).toString();
      return { label: year, value: year };
    }).reverse();
  }

  createMonthOptions(applianceCode: string): FormOption<number>[] {
    const purchaseYearControl = this.applianceForm
      .get(applianceCode)
      ?.get('purchaseYear') as FormControl<string | number | null> | null;

    const raw = purchaseYearControl?.value;
    const selectedYear =
      raw == null
        ? undefined
        : typeof raw === 'string'
          ? parseInt(raw, 10)
          : raw;

    return createMonthOptionsForYear(selectedYear);
  }

  readonly numberedAppliances = computed(
    () =>
      this.selectedAppliances()?.map((appliance, index) => ({
        ...appliance,
        displayTitle: `${index + 1}. ${appliance.applianceName}`,
      })) ?? [],
  );

  readonly radioFormFieldHasValue = computed(() => {
    return (code: string): boolean => {
      const control = this.applianceForm
        .get(code)
        ?.get('wasBoughtWithin12Months');
      return control instanceof FormControl && control.value !== null;
    };
  });

  readonly showMonthFormField = computed(() => {
    const map = this.shouldShowMonthByAppliance();
    return (code: string) => map[code] === true;
  });

  private createEmptyFormGroup(): FormGroup<Record<string, FormGroup>> {
    return new FormGroup<Record<string, FormGroup>>({});
  }

  formSubmitted = false;

  constructor() {
    effect(() => {
      const appliances = this.selectedAppliances();
      const isEmpty = Object.keys(this.applianceForm.controls).length === 0;

      if (appliances && isEmpty) {
        this.createApplianceForms(appliances);
      }
    });

    // Fetches the brands for each appliance code when selectedAppliances changes
    effect(() => {
      const appliances = this.selectedAppliances() || [];
      for (const appliance of appliances) {
        this.getBrandsForEachAppliance(appliance.applianceCode);
      }
    });
  }

  private createApplianceForms(appliances: SelectedAppliance[]): void {
    for (const appliance of appliances) {
      const applianceCode: string = appliance.applianceCode;
      const applianceForm: FormGroup<ApplianceDetailsForm> =
        this.applianceDetailsFormBuilder.build();
      const wasBoughtControl = applianceForm.get('wasBoughtWithin12Months');
      const purchaseMonthControl = applianceForm.get('purchaseMonth');
      const purchaseYearControl = applianceForm.get('purchaseYear');
      this.getBrandsForEachAppliance(applianceCode);

      this.updateMonthFieldVisibility(
        applianceCode,
        wasBoughtControl,
        purchaseMonthControl,
      );

      adjustMonthForYearSelection(
        purchaseYearControl,
        purchaseMonthControl,
        this.destroyRef,
      );

      this.applianceForm.addControl(applianceCode, applianceForm);
    }
  }

  //Get brands once for each appliance code and store them in a signal map
  private getBrandsForEachAppliance(applianceCode: string): void {
    const brandsByApplianceCodeCache = this.brandsByApplianceCode();

    if (
      !applianceCode ||
      Object.prototype.hasOwnProperty.call(
        brandsByApplianceCodeCache,
        applianceCode,
      ) ||
      this.loadingBrandsForApplianceCode.has(applianceCode)
    ) {
      return;
    }

    this.loadingBrandsForApplianceCode.add(applianceCode);

    this.catalogueService
      .getBrandsByApplianceCode(applianceCode)
      .pipe(
        map((brands) =>
          brands.map((brand) => ({
            label: brand.brandName,
            value: brand.brandCode,
          })),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (options) => {
          this.brandsByApplianceCode.update((curr) => ({
            ...curr,
            [applianceCode]: options,
          }));
          this.loadingBrandsForApplianceCode.delete(applianceCode);
        },
        error: () => {
          this.brandsByApplianceCode.update((curr) => ({
            ...curr,
            [applianceCode]: [],
          }));
          this.loadingBrandsForApplianceCode.delete(applianceCode);
        },
      });
  }

  private updateMonthFieldVisibility(
    code: string,
    wasBoughtControl: AbstractControl | null,
    purchaseMonthControl: AbstractControl | null,
  ): void {
    wasBoughtControl?.valueChanges.subscribe((value: boolean | null) => {
      const showMonth = value === true;

      this.shouldShowMonthByAppliance.update((prev) => ({
        ...prev,
        [code]: showMonth,
      }));

      if (showMonth) {
        purchaseMonthControl?.setValidators(
          FormControlValidators.required(
            this.translateService.translate(
              'APPLIANCE_DETAILS_FORM_PURCHASE_PRICE_REQUIRED',
            ),
          ),
        );
      } else {
        purchaseMonthControl?.clearValidators();
        purchaseMonthControl?.setValue(null);
      }

      purchaseMonthControl?.updateValueAndValidity();
    });
  }

  private getSelectedAppliancesWithDetails(): SelectedApplianceDetails[] {
    const formValue = this.applianceForm.value;
    const appliances = this.selectedAppliances();

    if (!appliances) {
      return [];
    }

    return appliances.map((appliance) => {
      const code = appliance.applianceCode;
      const formGroupValue: ApplianceDetailsFormValue = formValue[code];
      const currentManufacturer = formGroupValue.manufacturer;
      const currentMonth = getMonth(new Date()) + 1;

      const brandOptions = this.brandsByApplianceCode()[code];
      const matchedBrand = brandOptions.find(
        (b) => b.value === currentManufacturer,
      );
      const brandName = matchedBrand?.label ?? '';

      return {
        applianceCode: appliance.applianceCode,
        applianceName: appliance.applianceName,
        brandName,
        manufacturer: formGroupValue.manufacturer ?? null,
        purchaseMonth: formGroupValue.purchaseMonth ?? currentMonth,
        purchasePrice: formGroupValue.purchasePrice ?? null,
        purchaseYear: formGroupValue.purchaseYear ?? null,
        wasBoughtWithin12Months: formGroupValue.wasBoughtWithin12Months ?? null,
      };
    });
  }

  onSubmit() {
    posthog.capture(
      PosthogTrackingEvents.MultiPlanContinueButtonToReviewCoverPage,
    );

    if (this.applianceForm.invalid) {
      highlightInvalidControls(this.applianceForm);
      return;
    }

    this.formSubmitted = true;

    const selectedAppliancesWithDetails =
      this.getSelectedAppliancesWithDetails();

    this.multiPlanService.setSelectedAppliancesDetails(
      selectedAppliancesWithDetails,
    );

    this.basketService
      .addAppliancesToBasket(
        this.multiPlanService.addAppliancesToBasketPayload(),
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          this.trackingService.trackEvent(this.gaDataService.eventAddToCart());
          this.trackingService.trackEvent(
            this.gaDataService.eventBasketTotal(),
          );
          this.router.navigate(['/multi-plan/review-cover']);
        }),
        catchError(() => this.router.navigate([ErrorRoutes.Error])),
      )
      .subscribe();
  }

  onChangeAppliancesClick(): void {
    posthog.capture(
      PosthogTrackingEvents.MultiPlanApplianceDetailsFormChangeAppliances,
    );
  }
}
